/**
 * DevilThemeEffect — setea `<html data-theme="devil">` cuando el claim
 * `devil_dark_ui` está activo (tier "devil"). Componentes pueden engancharse
 * con Tailwind variants `data-[theme=devil]:*`.
 *
 * No renderiza nada — sólo gestiona el atributo en el root. Limpia en
 * unmount (logout, downgrade de tier, etc.) para que el theme no quede
 * pegado entre sesiones.
 */

import { useEffect } from "react";
import { useDevilDarkUi } from "@/lib/auth";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { useClerkGateMounted } from "@/lib/clerkGate";

const THEME_ATTR = "data-theme";
const DEVIL_THEME = "devil";

// Sin publishable key no se monta <ClerkProvider> (ver AppClerkProvider), y
// useDevilDarkUi() (→ useClerkSessionClaims → useAuth) lanzaría, tumbando el
// sitio público vía ErrorBoundary. Sin sesión Clerk no hay tier "devil": no-op.
const CLERK_ENABLED = Boolean(getClerkPublishableKey());

export function DevilThemeEffect() {
  // Gate dinámico: en la landing el provider se difiere (perf mobile).
  const clerkMounted = useClerkGateMounted();
  if (!CLERK_ENABLED || !clerkMounted) return null;
  return <DevilThemeEffectClerk />;
}

function DevilThemeEffectClerk() {
  const active = useDevilDarkUi();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const previous = root.getAttribute(THEME_ATTR);
    if (active) {
      root.setAttribute(THEME_ATTR, DEVIL_THEME);
    } else if (previous === DEVIL_THEME) {
      root.removeAttribute(THEME_ATTR);
    }
    return () => {
      if (active && root.getAttribute(THEME_ATTR) === DEVIL_THEME) {
        root.removeAttribute(THEME_ATTR);
      }
    };
  }, [active]);

  return null;
}
